import { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Section, Findings, Bullets, Prose, EvidenceBadge } from "./primitives";

const tone = (v, max = 10) => (v / max >= 0.75 ? "#22c55e" : v / max >= 0.5 ? "#d4af37" : "#dc2626");

export const Scorecard = ({ items }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    {(items || []).map((s) => (
      <div key={s.area} className="border border-[#222] bg-[#0a0a0a] p-3">
        <p className="rea-label">{s.area}</p>
        <p className="mt-1 font-display text-3xl font-bold" style={{ color: tone(s.score) }}>{s.score}<span className="text-sm text-neutral-600">/10</span></p>
        <div className="mt-1 h-1 bg-[#1f1f1f]"><div className="h-full" style={{ width: `${s.score * 10}%`, background: tone(s.score) }} /></div>
        <p className="mt-2 text-xs leading-snug text-neutral-400">{s.verdict}</p>
      </div>
    ))}
  </div>
);

export const ExecExtras = ({ e }) => (
  <>
    {e.scorecard?.length > 0 && <Section title="R.E.A. Scorecard" testid="exec-scorecard" className="lg:col-span-3"><Scorecard items={e.scorecard} /></Section>}
    {(e.audience || e.value_proposition || e.business_model) && (
      <Section title="Business Intelligence" testid="exec-business" className="lg:col-span-2">
        <div className="space-y-4">
          {e.value_proposition && <div><p className="rea-label mb-1">Value proposition</p><p className="text-sm text-neutral-200">{e.value_proposition}</p></div>}
          {e.audience && <div><p className="rea-label mb-1">Target audience</p><p className="text-sm text-neutral-200">{e.audience}</p></div>}
          {e.business_model && <div><p className="rea-label mb-1">Business model</p><Findings items={[e.business_model]} /></div>}
        </div>
      </Section>
    )}
    {e.rebuild_estimate && (
      <Section title="Rebuild Estimate" testid="exec-rebuild-estimate">
        <p className="font-display text-4xl font-bold text-[#d4af37]">{e.rebuild_estimate.hours_low}–{e.rebuild_estimate.hours_high}<span className="ml-1 text-base text-neutral-500">hrs</span></p>
        <p className="mt-1 font-mono text-xs uppercase tracking-widest text-neutral-400">{e.rebuild_estimate.team}</p>
        <p className="mt-3 text-sm text-neutral-400">{e.rebuild_estimate.rationale}</p>
        <div className="mt-3"><EvidenceBadge label="Inferred" /></div>
      </Section>
    )}
  </>
);

const ICON = { pass: [CheckCircle2, "text-green-500"], warn: [AlertTriangle, "text-amber-500"], fail: [XCircle, "text-red-500"] };

export const AuditPanel = ({ audits }) => {
  const cats = Object.keys(audits?.scores || {});
  const [cat, setCat] = useState(cats[0]);
  if (!cats.length) return null;
  return (
    <Section title="Measured Audit Scorecard (Verified)" testid="rec-audits" className="lg:col-span-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {cats.map((c) => (
          <button key={c} data-testid={`audit-cat-${c.toLowerCase()}`} onClick={() => setCat(c)} className={`border p-3 text-left transition-colors ${cat === c ? "border-red-600 bg-red-950/20" : "border-[#222] hover:border-neutral-600"}`}>
            <p className="rea-label">{c}</p>
            <p className="font-display text-3xl font-bold" style={{ color: tone(audits.scores[c], 100) }}>{audits.scores[c]}</p>
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-1.5">
        {audits.checks.filter((c) => c.category === cat).map((c) => {
          const [Icon, cls] = ICON[c.status];
          return (
            <div key={c.check} className="grid gap-1 border-b border-[#161616] py-2 sm:grid-cols-[22px_1fr_1.3fr] sm:gap-3">
              <Icon className={`h-4 w-4 ${cls}`} /><span className="text-sm text-white">{c.check}</span><span className="break-all font-mono text-[11px] text-neutral-400">{c.detail}</span>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-neutral-600">Deterministic checks measured from the live capture — not AI opinion.</p>
    </Section>
  );
};

export const DeepDive = ({ d }) => {
  if (!d) return null;
  const c = d.content_strategy || {};
  return (
    <>
      {d.analyst_verdict && (
        <Section title="R.E.A.'s Verdict" testid="deep-verdict" className="lg:col-span-2">
          <p className="font-ornate text-lg leading-relaxed text-neutral-100 sm:text-xl">"{d.analyst_verdict}"</p>
        </Section>
      )}
      <Section title="Security & Privacy" testid="deep-security"><Findings items={d.security_privacy} /></Section>
      <Section title="Accessibility" testid="deep-accessibility"><Findings items={d.accessibility} /></Section>
      <Section title="SEO" testid="deep-seo"><Findings items={d.seo} /></Section>
      <Section title="Performance Analysis" testid="deep-performance"><Findings items={d.performance_analysis} /></Section>
      <Section title="Content Strategy" testid="deep-content">
        {c.voice_tone && <p className="text-sm text-neutral-300"><span className="rea-label mr-2">Voice</span>{c.voice_tone}</p>}
        <p className="rea-label mb-2 mt-4">Messaging hierarchy</p><Bullets items={c.messaging_hierarchy} />
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div><p className="rea-label mb-2">CTAs</p><div className="flex flex-wrap gap-1.5">{(c.ctas || []).map((x) => <span key={x} className="border border-red-800 px-2 py-0.5 text-xs">{x}</span>)}</div></div>
          <div><p className="rea-label mb-2">Trust signals</p><div className="flex flex-wrap gap-1.5">{(c.trust_signals || []).map((x) => <span key={x} className="border border-[#d4af37]/40 px-2 py-0.5 text-xs">{x}</span>)}</div></div>
        </div>
        <p className="rea-label mb-2 mt-4">Content gaps</p><Bullets items={c.content_gaps} />
      </Section>
      <Section title="Conversion Funnel" testid="deep-funnel">
        <div className="space-y-2">{(d.conversion_funnel || []).map((f, i) => (
          <div key={i} className="border-l-2 border-red-700 bg-[#0a0a0a] p-3" style={{ marginLeft: i * 10 }}>
            <div className="flex justify-between gap-2"><p className="font-display font-semibold">{f.stage}</p><EvidenceBadge label={f.label} /></div>
            <p className="font-mono text-[11px] text-neutral-500">{(f.elements || []).join(" · ")}</p><p className="mt-1 text-sm text-neutral-300">{f.assessment}</p>
          </div>
        ))}</div>
      </Section>
      <Section title="Third-party Footprint" testid="deep-third-party">
        <div className="space-y-1.5">{(d.third_party_footprint || []).map((t, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-[#161616] py-1.5">
            <div><p className="font-mono text-sm text-white">{t.vendor}</p><p className="text-xs text-neutral-500">{t.purpose}</p></div>
            <span className={`font-mono text-[10px] uppercase ${t.privacy_impact === "High" ? "text-red-500" : t.privacy_impact === "Medium" ? "text-amber-500" : "text-green-500"}`}>{t.privacy_impact}</span>
            <EvidenceBadge label={t.label} />
          </div>
        ))}</div>
      </Section>
      {d.competitive_positioning && <Section title="Competitive Positioning" testid="deep-positioning"><Prose>{d.competitive_positioning}</Prose><div className="mt-3"><EvidenceBadge label="Inferred" /></div></Section>}
    </>
  );
};
